import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../../types';

interface ScheduledService {
  id: string;
  name: string;
  description?: string;
  companyId: string;
  company: {
    id: string;
    name: string;
  };
  responsibleId: string;
  responsible: {
    id: string;
    firstName: string;
    lastName: string;
  };
  categoryId: string;
  category: {
    id: string;
    name: string;
  };
  daysOfWeek: number[];
  isActive: boolean;
}

const daysOfWeek = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' }
];

const ScheduledServices: React.FC = () => {
  const [services, setServices] = useState<ScheduledService[]>([]);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState<ScheduledService | null>(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    companyId: '',
    responsibleId: '',
    categoryId: '',
    daysOfWeek: [] as number[],
    isActive: true
  });

  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const isManager = user?.role === UserRole.MANAGER;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchServices();
    fetchCompanies();
    fetchUsers();
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axiosInstance.get(`/scheduled-services?search=${search}`);
      setServices(response.data);
    } catch (error) {
      console.error('Error al obtener servicios programados:', error);
      alert('Error al obtener servicios programados');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al obtener empresas:', error);
      alert('Error al obtener empresas');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      alert('Error al obtener usuarios');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      alert('Error al obtener categorías');
    }
  };

  const handleOpenDialog = (service?: ScheduledService) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        description: service.description || '',
        companyId: service.companyId,
        responsibleId: service.responsibleId,
        categoryId: service.categoryId,
        daysOfWeek: service.daysOfWeek,
        isActive: service.isActive
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        companyId: '',
        responsibleId: user?.role === UserRole.MANAGER ? user.id : '',
        categoryId: '',
        daysOfWeek: [],
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axiosInstance.put(`/scheduled-services/${editingService.id}`, formData);
      } else {
        await axiosInstance.post('/scheduled-services', formData);
      }
      handleCloseDialog();
      fetchServices();
    } catch (error) {
      console.error('Error al guardar servicio programado:', error);
      alert('Error al guardar servicio programado');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este servicio programado?')) {
      try {
        await axiosInstance.delete(`/scheduled-services/${id}`);
        fetchServices();
      } catch (error) {
        console.error('Error al eliminar servicio programado:', error);
        alert('Error al eliminar servicio programado');
      }
    }
  };

  const handleDayToggle = (day: number) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Servicios Programados
        </Typography>
        {user?.role === UserRole.DIRECTOR && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
          >
            Nuevo Servicio Programado
          </Button>
        )}
      </Box>

      <Paper sx={{
        p: { xs: 1, sm: 2 },
        mb: { xs: 3, sm: 3 },
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        borderRadius: { xs: 3, sm: 2 },
        mt: { xs: 1, sm: 0 },
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TextField
          label="Buscar Servicios"
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2, flexShrink: 0 }}
        />
        <Box sx={{ overflowX: 'auto', width: '100%', flex: 1 }}>
          <TableContainer sx={{ width: '100%', minWidth: { xs: '600px', sm: 'auto' }, height: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Responsable</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Días</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service) => (
                  <TableRow
                    key={service.id}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/scheduled-services/${service.id}`, { state: { responsibleId: service.responsibleId } })}
                  >
                    <TableCell>{service.name}</TableCell>
                    <TableCell>{service.company.name}</TableCell>
                    <TableCell>{`${service.responsible.firstName} ${service.responsible.lastName}`}</TableCell>
                    <TableCell>{service.category.name}</TableCell>
                    <TableCell>
                      {service.daysOfWeek.map(day => (
                        <Chip
                          key={day}
                          label={daysOfWeek.find(d => d.value === day)?.label}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={service.isActive ? 'Activo' : 'Inactivo'}
                        color={service.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user?.role === UserRole.DIRECTOR || (user?.role === UserRole.MANAGER && service.responsibleId === user.id) ? (
                        <IconButton onClick={e => { e.stopPropagation(); handleOpenDialog(service); }}>
                          <EditIcon />
                        </IconButton>
                      ) : null}
                      {user?.role === UserRole.DIRECTOR && (
                        <IconButton onClick={e => { e.stopPropagation(); handleDelete(service.id); }}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingService ? 'Editar Servicio Programado' : 'Nuevo Servicio Programado'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={formData.companyId}
                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                    required
                  >
                    {companies.map((company: any) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Responsable</InputLabel>
                  <Select
                    value={formData.responsibleId}
                    onChange={(e) => setFormData({ ...formData, responsibleId: e.target.value })}
                    required
                    disabled={user?.role === UserRole.MANAGER}
                    sx={user?.role === UserRole.MANAGER ? { backgroundColor: '#f5f5f5', color: '#888' } : {}}
                  >
                    {user?.role === UserRole.MANAGER ? (
                      <MenuItem value={user.id}>
                        {user.firstName} {user.lastName} (Tú)
                      </MenuItem>
                    ) : (
                      users.length === 0 ? (
                        <MenuItem value="" disabled>No hay usuarios disponibles</MenuItem>
                      ) : (
                        users.map((user: any) => (
                          <MenuItem key={user.id} value={user.id}>
                            {`${user.firstName} ${user.lastName}`}
                          </MenuItem>
                        ))
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    required
                  >
                    {categories.map((category: any) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Días de la semana
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {daysOfWeek.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={formData.daysOfWeek.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingService ? 'Guardar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ScheduledServices; 